import React, { useCallback, useEffect, useRef, useState } from 'react';

export type ChatMessage = {
  from: 'you' | 'aspen';
  text: string;
  /** Beat before this message lands. Aspen's beats double as typing time. */
  delay?: number;
};

interface PhoneChatProps {
  messages: ChatMessage[];
  /** Sentence describing the exchange for screen readers and search engines. */
  label: string;
  /** Date divider at the top of the thread, e.g. "Today 9:41 AM". */
  timeLabel?: string;
  /** Status bar clock. */
  statusTime?: string;
  /** Replay forever instead of stopping with a replay button. */
  loop?: boolean;
  /** Called a beat after the last message lands. Drives the step carousel. */
  onFinished?: () => void;
  className?: string;
}

const DEFAULT_YOU_DELAY = 1000;
const DEFAULT_ASPEN_DELAY = 1600;
const LOOP_PAUSE = 4200;
const FINISHED_HOLD = 3600;

/**
 * An iPhone-shaped Messages thread that plays itself once it scrolls into view.
 * The phone stays dark in both site themes — a phone looks like a phone.
 */
const PhoneChat: React.FC<PhoneChatProps> = ({
  messages,
  label,
  timeLabel = 'Today',
  statusTime = '9:41',
  loop = false,
  onFinished,
  className = '',
}) => {
  const [revealed, setRevealed] = useState(0);
  const [typing, setTyping] = useState(false);
  const [started, setStarted] = useState(false);
  const [reduced, setReduced] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const onFinishedRef = useRef(onFinished);

  useEffect(() => {
    onFinishedRef.current = onFinished;
  }, [onFinished]);

  // Honor reduced-motion by showing the finished thread instead of animating it.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = (matches: boolean) => {
      setReduced(matches);
      if (matches) {
        setTyping(false);
        setRevealed(messages.length);
      }
    };
    apply(query.matches);

    const listener = (event: MediaQueryListEvent) => apply(event.matches);
    if (query.addEventListener) {
      query.addEventListener('change', listener);
      return () => query.removeEventListener('change', listener);
    }
    query.addListener(listener);
    return () => query.removeListener(listener);
  }, [messages.length]);

  // Nothing plays until the phone is actually on screen.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || started || reduced) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [started, reduced]);

  // One timer at a time: type, land the message, move to the next.
  useEffect(() => {
    if (!started || reduced) {
      return;
    }

    if (revealed >= messages.length) {
      if (loop) {
        const restart = window.setTimeout(() => setRevealed(0), LOOP_PAUSE);
        return () => window.clearTimeout(restart);
      }
      if (onFinishedRef.current) {
        const advance = window.setTimeout(() => onFinishedRef.current?.(), FINISHED_HOLD);
        return () => window.clearTimeout(advance);
      }
      return;
    }

    const next = messages[revealed];
    const isAspen = next.from === 'aspen';
    const wait = next.delay ?? (isAspen ? DEFAULT_ASPEN_DELAY : DEFAULT_YOU_DELAY);

    if (isAspen) {
      setTyping(true);
    }
    const timer = window.setTimeout(() => {
      setTyping(false);
      setRevealed((count) => count + 1);
    }, wait);

    return () => window.clearTimeout(timer);
  }, [started, reduced, revealed, messages, loop]);

  const replay = useCallback(() => {
    setTyping(false);
    setRevealed(0);
    setStarted(true);
  }, []);

  const visible = messages.slice(0, revealed);
  const finished = revealed >= messages.length && !typing;
  const lastIsYours = visible.length > 0 && visible[visible.length - 1].from === 'you';

  return (
    <div ref={containerRef} className={`relative ${onFinished ? '' : 'pb-9'} ${className}`}>
      {/* Screen readers and crawlers get the whole conversation, always. */}
      <p className="sr-only">{label}</p>
      <ol className="sr-only">
        {messages.map((message) => (
          <li key={`${message.from}-${message.text}`}>
            {message.from === 'you' ? 'You: ' : 'Aspen: '}
            {message.text}
          </li>
        ))}
      </ol>

      <div
        aria-hidden="true"
        className="relative mx-auto w-full max-w-[300px] rounded-[2.75rem] bg-[#0b0b0d] p-[10px] shadow-[0_28px_55px_rgba(15,23,42,0.35)] ring-1 ring-white/10"
      >
        <div className="relative flex aspect-[1290/2620] w-full flex-col overflow-hidden rounded-[2.15rem] bg-black">
          {/* Status bar */}
          <div className="relative z-20 flex items-center justify-between bg-[#1c1c1e] px-6 pt-3 text-[11px] font-semibold text-white">
            <span>{statusTime}</span>
            <div className="absolute left-1/2 top-2 h-[22px] w-[76px] -translate-x-1/2 rounded-full bg-black" />
            <span className="flex items-center gap-1">
              <svg viewBox="0 0 18 12" className="h-[9px] w-[13px] fill-white">
                <rect x="0" y="8" width="3" height="4" rx="1" />
                <rect x="5" y="5.5" width="3" height="6.5" rx="1" />
                <rect x="10" y="3" width="3" height="9" rx="1" />
                <rect x="15" y="0" width="3" height="12" rx="1" opacity="0.4" />
              </svg>
              <svg viewBox="0 0 26 13" className="h-[11px] w-[22px]">
                <rect x="0.5" y="0.5" width="21" height="12" rx="3.5" className="fill-none stroke-white/40" />
                <rect x="2" y="2" width="15" height="9" rx="2" className="fill-white" />
                <path d="M23.5 4.5v4a2.5 2.5 0 0 0 0-4Z" className="fill-white/40" />
              </svg>
            </span>
          </div>

          {/* Conversation header */}
          <div className="z-20 flex flex-col items-center border-b border-white/10 bg-[#1c1c1e] px-4 pb-2 pt-1">
            <div className="flex w-full items-center justify-between">
              <span className="text-[15px] font-medium text-[#0a84ff]">&lsaquo;</span>
              <span className="text-[15px] text-[#0a84ff]" aria-hidden="true">
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
                  <path d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 3v-9l-4 3Z" />
                </svg>
              </span>
            </div>
            <div className="-mt-4 flex flex-col items-center">
              <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white">
                <img src="/aspen-logomark.webp" alt="" width={32} height={32} className="h-full w-full object-cover" />
              </span>
              <span className="mt-0.5 flex items-center gap-0.5 text-[11px] font-medium text-white">
                Aspen
                <svg viewBox="0 0 24 24" className="h-2.5 w-2.5 stroke-white/50" fill="none" strokeWidth={3}>
                  <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
          </div>

          {/* Thread — anchored to the bottom so new messages push older ones up */}
          <div
            className="relative z-10 flex flex-1 flex-col justify-end gap-1.5 overflow-hidden px-3 pb-2"
            style={{
              maskImage: 'linear-gradient(to bottom, transparent 0, rgba(0,0,0,0.15) 26px, black 62px)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, rgba(0,0,0,0.15) 26px, black 62px)',
            }}
          >
            {revealed > 0 && (
              <p className="animate-bubble-in pb-1 text-center text-[10px] font-medium text-white/40">
                {timeLabel}
              </p>
            )}

            {visible.map((message, index) => (
              <div
                key={`${message.from}-${index}`}
                className={`animate-bubble-in flex ${message.from === 'you' ? 'justify-end' : 'justify-start'}`}
              >
                <p
                  className={
                    message.from === 'you'
                      ? 'max-w-[80%] rounded-[18px] rounded-br-[6px] bg-[#30d158] text-left px-3 py-2 text-[13px] leading-snug text-white'
                      : 'max-w-[80%] rounded-[18px] rounded-bl-[6px] bg-[#26262a] text-left px-3 py-2 text-[13px] leading-snug text-white'
                  }
                >
                  {message.text}
                </p>
              </div>
            ))}

            {typing && (
              <div className="animate-bubble-in flex justify-start">
                <span className="flex items-center gap-1 rounded-[18px] rounded-bl-[6px] bg-[#26262a] px-3.5 py-3">
                  {[0, 1, 2].map((dot) => (
                    <span
                      key={dot}
                      className="h-1.5 w-1.5 animate-typing-dot rounded-full bg-white"
                      style={{ animationDelay: `${dot * 160}ms` }}
                    />
                  ))}
                </span>
              </div>
            )}

            {finished && lastIsYours && (
              <p className="pr-1 text-right text-[9px] font-medium text-white/40">Delivered</p>
            )}
          </div>

          {/* Compose bar */}
          <div className="relative z-10 flex items-center gap-2 bg-black px-3 pb-3 pt-1">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#26262a] text-[15px] leading-none text-white/60">
              +
            </span>
            <span className="flex h-7 flex-1 items-center rounded-full border border-white/15 px-3 text-[11px] text-white/35">
              Text Message
            </span>
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#30d158]">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-white">
                <path d="M12 4l7 7h-4.5v9h-5v-9H5l7-7Z" />
              </svg>
            </span>
          </div>

          {/* Home indicator */}
          <div className="relative z-10 mx-auto mb-1.5 h-1 w-24 rounded-full bg-white/30" />
        </div>
      </div>

      {finished && !loop && !reduced && !onFinished && (
        <button
          type="button"
          onClick={replay}
          className="absolute bottom-0 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-white/95 px-3.5 py-1.5 text-xs font-semibold text-aspen-deep-eucalyptus shadow-md ring-1 ring-black/5 transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-aspen-vibrant-blue dark:bg-aspen-twilight-teal/95 dark:text-aspen-pearl dark:ring-white/10"
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current">
            <path d="M12 5V2L7 6l5 4V7a5 5 0 1 1-5 5H5a7 7 0 1 0 7-7Z" />
          </svg>
          Replay
        </button>
      )}
    </div>
  );
};

export default PhoneChat;
